import React, { useEffect, useState, useMemo } from "react";
import { axiosInstance } from "../lib/axios";
import { Pin, PinOff, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STAGES = ["Design", "Approval", "Construction"];

const CategoryProjectsPanel = ({ selectedCategory }) => {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [pinned, setPinned] = useState(() => {
    const saved = localStorage.getItem("pinnedProjects");
    return saved ? JSON.parse(saved) : {};
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  /* --------- Mobile Resize --------- */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* --------- Fetch Projects --------- */
  useEffect(() => {
    if (!selectedCategory) return;
    const fetchProjects = async () => {
      try {
        const res = await axiosInstance.get(
          `/projects?category=${encodeURIComponent(selectedCategory)}`
        );
        const data = res.data?.projects || res.data || [];
        setProjects(data);
      } catch (err) {
        console.error("❌ Failed to load category projects:", err);
      }
    };
    fetchProjects();
  }, [selectedCategory]);

  /* --------- Helpers --------- */
  const categoryKey = selectedCategory?.toUpperCase() || "";
  const getPins = () => pinned[categoryKey] || [];

  const isProjectStagePinned = (projectId, stage) =>
    getPins().some((p) => p.projectId === projectId && p.stage === stage);

  const togglePin = (projectId, stage = "General") => {
    const currentPins = getPins();
    const existing = currentPins.find(
      (p) => p.projectId === projectId && p.stage === stage
    );

    let updatedPins;
    if (existing) {
      updatedPins = currentPins.filter(
        (p) => !(p.projectId === projectId && p.stage === stage)
      );
    } else {
      updatedPins = [...currentPins, { projectId, stage }];
    }

    const updated = { ...pinned, [categoryKey]: updatedPins };
    setPinned(updated);
    localStorage.setItem("pinnedProjects", JSON.stringify(updated));
  };

  /* --------- Sorting / Filtering --------- */
  const sortedProjects = useMemo(() => {
    const pins = getPins().map((p) => p.projectId);
    return [
      ...projects.filter((p) => pins.includes(p._id)),
      ...projects.filter((p) => !pins.includes(p._id)),
    ];
  }, [projects, pinned, categoryKey]);

  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return sortedProjects;
    return sortedProjects.filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, sortedProjects]);

  /* --------- Render --------- */
  if (!selectedCategory)
    return (
      <div style={{ color: "#888", padding: 10 }}>
        Select a category to view projects
      </div>
    );

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fafbff",
        borderLeft: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* ========== Header ========== */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#fafbff",
          padding: isMobile ? "10px 10px 8px 10px" : "12px 12px 8px 12px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <h3
          style={{
            fontSize: isMobile ? 14 : 15,
            color: "#343a76",
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          📁 Category-wise Projects
        </h3>

        {/* Search */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: 8,
            padding: "6px 10px",
            gap: 6,
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <Search size={16} color="#555" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 13,
              padding: 2,
            }}
          />
        </div>
      </div>

      {/* ========== Scroll List ========== */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: isMobile ? "8px 10px" : "10px 12px",
        }}
      >
        {filteredProjects.length === 0 ? (
          <div style={{ color: "#999", fontSize: 13 }}>
            No matching projects found.
          </div>
        ) : (
          filteredProjects.map((proj) => {
            const isGeneralPinned = isProjectStagePinned(proj._id, "General");

            return (
              <div
                key={proj._id}
                onClick={() =>
                  navigate(
                    `/messenger/project-communication?projectId=${
                      proj._id
                    }&name=${encodeURIComponent(proj.name)}`
                  )
                }
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: isMobile ? "8px 10px" : "10px 12px",
                  marginBottom: 8,
                  background: "#fff",
                  cursor: "pointer",
                  wordBreak: "break-word",
                  transition: "0.2s",
                  ...(isMobile
                    ? {}
                    : {
                        onHoverBackground: "#f5f8ff",
                      }),
                }}
                onMouseEnter={(e) =>
                  !isMobile && (e.currentTarget.style.background = "#f5f8ff")
                }
                onMouseLeave={(e) =>
                  !isMobile && (e.currentTarget.style.background = "#fff")
                }
              >
                {/* Project Name */}
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#222",
                    marginBottom: 6,
                  }}
                >
                  {proj.name}
                </div>

                {/* 📌 General Pin */}
                {selectedCategory?.toUpperCase() !== "ARCH.IND" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(proj._id, "General");
                    }}
                    title={isGeneralPinned ? "Unpin project" : "Pin project"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      background: isGeneralPinned ? "#e7f1ff" : "#f9f9f9",
                      border: `1px solid ${
                        isGeneralPinned ? "#0d6efd" : "#ccc"
                      }`,
                      color: isGeneralPinned ? "#0d6efd" : "#444",
                      fontSize: 12,
                      padding: "4px 8px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    {isGeneralPinned ? (
                      <Pin fill="#0d6efd" size={14} />
                    ) : (
                      <PinOff size={14} />
                    )}
                    Pin
                  </button>
                )}

                {/* Stage Pins (ARCH.IND only) */}
                {selectedCategory?.toUpperCase() === "ARCH.IND" && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {STAGES.map((stage) => {
                      const pinnedStage = isProjectStagePinned(proj._id, stage);
                      return (
                        <button
                          key={stage}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePin(proj._id, stage);
                          }}
                          title={
                            pinnedStage
                              ? `Unpin ${stage} stage`
                              : `Pin ${stage} stage`
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            background: pinnedStage ? "#e7f1ff" : "#f9f9f9",
                            border: `1px solid ${
                              pinnedStage ? "#0d6efd" : "#ccc"
                            }`,
                            color: pinnedStage ? "#0d6efd" : "#444",
                            fontSize: 12,
                            padding: "4px 8px",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          {pinnedStage ? (
                            <Pin fill="#0d6efd" size={14} />
                          ) : (
                            <PinOff size={14} />
                          )}
                          {stage}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CategoryProjectsPanel;
